import { inject, Injectable, Injector } from '@angular/core';
import { AudioService } from '@core/audio/audio.service';
import { GameService } from '@core/game/game.service';
import { AssetReaderService } from '@core/lp/asset-reader.service';
import { LpAudioService } from '@features/lp/lp-audio/lp-audio.service';
import { LpConditionsService } from '@features/lp/lp-conditions/lp-conditions.service';
import { LpJuiceService } from '@features/lp/lp-juice/lp-juice.service';
import { LpMissionService } from '@features/lp/lp-mission/lp-mission.service';
import { LpNotificationsService } from '@features/lp/lp-notifications/lp-notifications.service';
import { LpTriggersService } from '@features/lp/lp-triggers/lp-triggers.service';
import { LpVariablesService } from '@features/lp/lp-variables/lp-variables.service';
import { waitForMillis } from '@shared/delay.utils';
import { Uid } from '@typings/id';
import { LpAction, LpEvent } from '@typings/lp/generated';

@Injectable({
  providedIn: 'root',
})
export class LpActionsService {
  readonly #injector = inject(Injector);

  get _audioService() {
    return this.#injector.get(AudioService);
  }

  get _conditionsService() {
    return this.#injector.get(LpConditionsService);
  }

  get _gameService() {
    return this.#injector.get(GameService);
  }

  get _missionsService() {
    return this.#injector.get(LpMissionService);
  }

  get _assetReaderService() {
    return this.#injector.get(AssetReaderService);
  }

  get _lpAudioService() {
    return this.#injector.get(LpAudioService);
  }

  get _lpJuiceService() {
    return this.#injector.get(LpJuiceService);
  }

  get _lpNotificationsService() {
    return this.#injector.get(LpNotificationsService);
  }

  get _lpTriggersService() {
    return this.#injector.get(LpTriggersService);
  }

  get _lpVariablesService() {
    return this.#injector.get(LpVariablesService);
  }

  public async perform(event: LpEvent | undefined): Promise<void> {
    if (!event) return;

    for (const action of event.actions) {
      await this.performAction(action);
    }
  }

  private async performAction(action: LpAction): Promise<void> {
    // NOTE: try to keep the order of the if statements alphabetical

    if (action.dataType === 'DelayAction') {
      await waitForMillis(action.seconds * 1000);
      return;
    }

    if (action.dataType === 'FinishActivityAction') {
      const activity = this._gameService.activity();
      if (!activity) return;

      await this._missionsService.finishActivity(activity);
      return;
    }

    if (action.dataType === 'GetItemAction') {
      if (!action.item) return; // TODO: shouldn't item be required?

      await this._gameService.setInventory([...this._gameService.inventoryUids(), action.item.uid as Uid]);
      this._audioService.play('/assets/audio/grab.wav');
      return;
    }

    if (action.dataType === 'IfThenAction') {
      if (await this._conditionsService.evaluate(action.if)) {
        await this.perform(action.then);
      }
      return;
    }

    if (action.dataType === 'IfThenElseAction') {
      if (await this._conditionsService.evaluate(action.if)) {
        await this.perform(action.then);
      } else {
        await this.perform(action.else);
      }
      return;
    }

    if (action.dataType === 'LoseItemAction') {
      if (!action.item) return; // TODO: shouldn't item be required

      await this._gameService.setInventory([...this._gameService.inventoryUids().filter((uid) => uid !== action.item?.uid)]);
      return;
    }

    if (action.dataType === 'PlayAudioAction') {
      await this._lpAudioService.play(action.audio, action.waitUntilComplete, action.audioConfiguration);
      return;
    }

    if (action.dataType === 'PlayJuiceAction') {
      await this._lpJuiceService.playComposition(action.juiceComposition, action.screenPosition);
      return;
    }

    if (action.dataType === 'PlayStoryAction') {
      await this._gameService.playStory(action.story, action.waitUntilComplete);
      return;
    }

    if (action.dataType === 'RunTriggerAction') {
      await this._lpTriggersService.run(action.trigger);
      return;
    }

    if (action.dataType === 'SetVariableAction') {
      this._lpVariablesService.set(action.variable.variableRef, action.newValue);
      return;
    }

    if (action.dataType === 'ShowItemContentAction') {
      await this._gameService.showItemContent(action.item, true);
      return;
    }

    if (action.dataType === 'ShowNotificationAction') {
      await this._lpNotificationsService.show(action.title, action.message.length > 0 ? action.message : undefined);
      return;
    }

    if (action.dataType === 'StartMissionAction') {
      await this._missionsService.start(action.mission);
      return;
    }

    if (action.dataType === 'SwitchMapAction') {
      await this._gameService.setMap(action.map.uid as Uid);
      return;
    }

    throw new Error(`Action '${action.dataType}' not supported`);
  }
}
